/**
 * Shared viewport gate primitives.
 *
 * Both the Drawing module (Phase 4) and the Spec module (Phase 4) are
 * desktop-only — anything below 1280px renders a stub instead of the
 * workbench. This module owns the constant + the resize hook + the
 * stub component so the two callers stay in lockstep on copy and
 * threshold.
 *
 * Per `Specs/SPEC_HANDOFF.md` decision #20 / pre-flight item D-4:
 * extracted from drawing's inline gate in `DrawingPage.tsx` so Spec
 * Phase 4 reuses without copy-paste.
 */
import * as React from 'react';
import { Monitor } from 'lucide-react';

export const VIEWPORT_MIN_WIDTH_PX = 1280;

/**
 * Subscribe to window resize events and return whether the current
 * viewport is below ``minWidthPx``. SSR-safe: returns ``false`` on the
 * server, then re-evaluates on first client render.
 */
export function useViewportGate(minWidthPx: number = VIEWPORT_MIN_WIDTH_PX): boolean {
  const [tooNarrow, setTooNarrow] = React.useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < minWidthPx : false,
  );

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setTooNarrow(window.innerWidth < minWidthPx);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minWidthPx]);

  return tooNarrow;
}

interface ViewportTooNarrowProps {
  /**
   * Module label surfaced in the heading
   * (e.g. ``"Drawing workbench"`` / ``"Specification workbench"``).
   */
  moduleName: string;
  /**
   * Optional explanation of which surfaces are crowded by the narrow
   * viewport. The shared default is module-agnostic; callers tighten
   * the wording where it helps.
   */
  detail?: string;
  /** Optional header element rendered above the stub body. */
  header?: React.ReactNode;
  /**
   * data-testid for assertions. Drawing uses
   * ``"drawings-viewport-stub"``; Spec uses ``"spec-viewport-stub"``.
   */
  testId?: string;
  /**
   * Override the threshold surfaced in the body copy. Defaults to
   * ``VIEWPORT_MIN_WIDTH_PX``.
   */
  minWidthPx?: number;
}

/**
 * Standard "open this on a desktop" stub. Same shape both modules
 * rendered inline before the extraction; copy is module-agnostic by
 * default and tightenable via ``detail``.
 */
export function ViewportTooNarrow({
  moduleName,
  detail,
  header,
  testId,
  minWidthPx = VIEWPORT_MIN_WIDTH_PX,
}: ViewportTooNarrowProps): JSX.Element {
  return (
    <div className="min-h-screen bg-zinc-50">
      {header}
      <div
        className="mx-auto max-w-xl px-6 py-16 text-center"
        data-testid={testId}
      >
        <Monitor className="mx-auto mb-4 h-10 w-10 text-zinc-400" aria-hidden />
        <h2 className="mb-2 text-base font-semibold text-zinc-900">
          {moduleName} is desktop-only
        </h2>
        <p className="text-sm text-zinc-600">
          {detail
            ? detail
            : `This workbench needs at least ${minWidthPx}px of horizontal space. ` +
              'Open this matter on a desktop to continue.'}
        </p>
      </div>
    </div>
  );
}

export default ViewportTooNarrow;
